import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import styles from "./Category.module.css";
import Spin from "../Spinner/Spinner";
import { API } from "../../config";
import { FaPlus } from "react-icons/fa";
import Toggle from "../../utils/Toggle/Toggle";

// DnD Kit imports
import {
  DndContext,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";

import {
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
  arrayMove,
} from "@dnd-kit/sortable";
import { RxDragHandleHorizontal } from "react-icons/rx";
import { FaPencilAlt } from "react-icons/fa";

import { CSS } from "@dnd-kit/utilities";
function SortableRow({ c, onToggleStatus }) {
  const { attributes, listeners, setNodeRef, transform, transition } =
    useSortable({ id: c._id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  return (
    <tr ref={setNodeRef} style={style} className={styles.testimonialrow}>
      <td>
        <DragHandle listeners={listeners} attributes={attributes} />
      </td>

      <td>{c.sortId}</td>

      <td className={styles.categoryname}>
        <img src={c.images[0]} alt="Logo" className={styles.logos} />
      </td>

      <td>{c.name}</td>
      <td>{c.description}</td>
      <td>{c.custom ? "True" : "False"}</td>

      <td>
        <Toggle
          status={c.isActive}
          setStatus={() => onToggleStatus(c._id, c.isActive)}
        />
      </td>

      <td className={styles.edit}>
        <Link to={`/category/${c._id}`}>
          <div>Edit</div>
        </Link>
      </td>
    </tr>
  );
}

function Category() {
  const [categories, setCategories] = useState([]);
  const [spin, setSpin] = useState(true);
  const [originalOrder, setOriginalOrder] = useState([]);
  const [isChanged, setIsChanged] = useState(false);

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: { distance: 5 },
    })
  );

  const getData = async () => {
    const data = await fetch(`${API}/Admin/category/get`);
    const json = await data.json();

    const sorted = json.allcategory.sort((a, b) => a.sortId - b.sortId);

    setCategories(sorted);
    setOriginalOrder(sorted.map((c) => c._id));
    setSpin(false);
  };

  const updateCategoryOrder = async ({ order }) => {
    try {
      const data = await fetch(`${API}/category/order`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ order }),
      });
      const json = await data.json();
      console.log("Updated:", json);
    } catch (e) {
      console.error("Update failed:", e);
    }
  };

  const handleDragEnd = ({ active, over }) => {
    if (!over || active.id === over.id) return;

    setCategories((prev) => {
      const oldIndex = prev.findIndex((item) => item._id === active.id);
      const newIndex = prev.findIndex((item) => item._id === over.id);

      const newArray = arrayMove(prev, oldIndex, newIndex);

      newArray.forEach((item, idx) => {
        item.sortId = idx + 1;
      });

      const newOrderIds = newArray.map((c) => c._id);

      setIsChanged(
        JSON.stringify(newOrderIds) !== JSON.stringify(originalOrder)
      );

      return [...newArray];
    });
  };

  const toggleStatus = async (categoryId, currentStatus) => {
    setCategories((prev) =>
      prev.map((c) =>
        c._id === categoryId ? { ...c, isActive: !currentStatus } : c
      )
    );
    try {
      await fetch(`${API}/category/${categoryId}/toggle-status`, {
        method: "PATCH",
      });
    } catch (e) {
      // revert on failure
      setCategories((prev) =>
        prev.map((c) =>
          c._id === categoryId ? { ...c, isActive: currentStatus } : c
        )
      );
      console.error("Toggle failed:", e);
    }
  };

  const handleSave = () => {
    const payload = categories.map((item) => ({
      categoryId: item._id,
      order: item.sortId,
    }));

    updateCategoryOrder({ order: payload });

    setOriginalOrder(categories.map((c) => c._id));
    setIsChanged(false);
  };

  useEffect(() => {
    getData();
  }, []);

  return (
    <>
      {spin ? (
        <Spin />
      ) : (
        <div className={styles.entirepage}>
          <div className={styles.upper}>
            <Link to="/">
              <span className={styles.swag}>
                Website <span className={styles.greater}> &gt; </span>
              </span>
            </Link>
            <span className={styles.req}>Categories</span>
          </div>

          <div className={styles.heading}>Categories</div>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "10px",
              justifyContent: "space-between",
              flexDirection: "row",
            }}
          >
            <div className={styles.addingbutton}>
              <Link to="/category/add">
                <div className={styles.button}>
                  <FaPlus className={styles.addicon} />
                  Add Category
                </div>
              </Link>
            </div>

            <button
              onClick={handleSave}
              disabled={!isChanged}
              className={styles.button}
              style={{ width: "fit-content" }}
            >
              Save Order
            </button>
          </div>

          <div className={styles.logosdisplay}>
            <table className={styles.displaytable}>
              <thead>
                <tr className={styles.testimonialrowhead}>
                  <th></th>
                  <th>S No.</th>
                  <th></th>
                  <th>Category</th>
                  <th>Description</th>
                  <th>Customisation</th>
                  <th>Status</th>
                  <th></th>
                </tr>
              </thead>

              {/* DND CONTEXT */}
              <DndContext
                sensors={sensors}
                collisionDetection={closestCenter}
                onDragEnd={handleDragEnd}
              >
                <SortableContext
                  items={categories.map((c) => c._id)}
                  strategy={verticalListSortingStrategy}
                >
                  <tbody>
                    {categories.map((c) => (
                      <SortableRow c={c} key={c._id} onToggleStatus={toggleStatus} />
                    ))}
                  </tbody>
                </SortableContext>
              </DndContext>
            </table>
          </div>
        </div>
      )}
    </>
  );
}

const DragHandle = ({ listeners, attributes }) => {
  return (
    <span
      {...listeners}
      {...attributes}
      style={{
        cursor: "grab",
        padding: "8px",
        display: "inline-block",
        fontSize: "18px",
        userSelect: "none",
      }}
    >
      <RxDragHandleHorizontal />
    </span>
  );
};

export default Category;
